// react-redux imports
import { MouseEvent, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "app/store";

// styled-component imports
import UserInputSection from "../styled-components/UserInputSection";

// common imports
import CircleButton from "common/components/CircleButton";
import CircleButtonContainer from "common/components/styled-components/CircleButtonContainer";
import CircleButtonGroupLabel from "common/components/styled-components/CircleButtonGroupLabel";
import CircleButtonGroupWithLabel from "common/components/styled-components/CircleButtonGroupWithLabel";
import FlexSpan from "common/components/styled-components/FlexSpan";
import SectionTitle from "common/components/styled-components/SectionTitle";
import Tooltip from "common/components/Tooltip";
import TooltipPopUp from "common/components/TooltipPopUp";

import { FLIGHT_OPTIONS } from "../constants/transport";
import {
  setNationalFlightsYearly,
  setEuropeanFlightsYearly,
  setInternationalFlightsYearly,
} from "./transportSlice";
import PopUpParagraph from "common/components/styled-components/PopUpParagraph";
import { TOOLTIP_ACTIONS } from "common/constants/tooltip";

function StepThree() {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const dispatch = useDispatch();

  const nationalFlightsYearly = useSelector(
    (state: RootState) => state.calculator.transport.nationalFlightsYearly
  );

  const europeanFlightsYearly = useSelector(
    (state: RootState) => state.calculator.transport.europeanFlightsYearly
  );

  const internationalFlightsYearly = useSelector(
    (state: RootState) => state.calculator.transport.internationalFlightsYearly
  );

  function handleNationalFlightsSelection(e: MouseEvent<HTMLButtonElement>) {
    const value = Number(e.currentTarget.value);
    if (value === nationalFlightsYearly)
      dispatch(setNationalFlightsYearly(null));
    else dispatch(setNationalFlightsYearly(value));
  }

  function handleEuropeanFlightsSelection(e: MouseEvent<HTMLButtonElement>) {
    const value = Number(e.currentTarget.value);
    if (value === europeanFlightsYearly)
      dispatch(setEuropeanFlightsYearly(null));
    else dispatch(setEuropeanFlightsYearly(value));
  }

  function handleInternationalFlightsSelection(
    e: MouseEvent<HTMLButtonElement>
  ) {
    const value = Number(e.currentTarget.value);
    if (value === internationalFlightsYearly)
      dispatch(setInternationalFlightsYearly(null));
    else dispatch(setInternationalFlightsYearly(value));
  }

  type TooltipAction = "open" | "close" | "toggle";
  function handleTooltip(action: TooltipAction) {
    switch (action) {
      case "open":
        return () => setIsTooltipOpen(true);

      case "close":
        return () => setIsTooltipOpen(false);

      case "toggle":
        return () => setIsTooltipOpen(!isTooltipOpen);
    }
  }

  return (
    <>
      <SectionTitle>
        <span>
          ¿Cuántos viajes haces al año en{" "}
          <FlexSpan>
            avión?&nbsp;
            <Tooltip onClick={handleTooltip("toggle")} />
          </FlexSpan>
        </span>
        {isTooltipOpen && (
          <TooltipPopUp
            title="¿Cuántos viajes haces al año en avión?"
            handleClose={handleTooltip(TOOLTIP_ACTIONS.CLOSE)}
          >
            <PopUpParagraph>
              Es importante conocer cuántos viajes al año realizas ya que el
              avión es el medio de transporte que más contamina, ascendiendo a
              285 grs de CO2 por Km y pasajero. Por ejemplo en un viaje.
            </PopUpParagraph>
          </TooltipPopUp>
        )}
      </SectionTitle>
      <UserInputSection>
        <CircleButtonGroupWithLabel>
          <CircleButtonGroupLabel>
            Vuelos nacionales al año
          </CircleButtonGroupLabel>
          <CircleButtonContainer>
            {FLIGHT_OPTIONS.map((option) => (
              <CircleButton
                key={"national__" + option.value}
                value={option.value}
                text={option.label}
                selected={option.value === nationalFlightsYearly}
                onClick={handleNationalFlightsSelection}
              />
            ))}
          </CircleButtonContainer>
        </CircleButtonGroupWithLabel>
        <CircleButtonGroupWithLabel>
          <CircleButtonGroupLabel>
            Vuelos europeos al año
          </CircleButtonGroupLabel>
          <CircleButtonContainer>
            {FLIGHT_OPTIONS.map((option) => (
              <CircleButton
                key={"european__" + option.value}
                value={option.value}
                text={option.label}
                selected={option.value === europeanFlightsYearly}
                onClick={handleEuropeanFlightsSelection}
              />
            ))}
          </CircleButtonContainer>
        </CircleButtonGroupWithLabel>
        <CircleButtonGroupWithLabel>
          <CircleButtonGroupLabel>
            Vuelos internacionales al año
          </CircleButtonGroupLabel>
          <CircleButtonContainer>
            {FLIGHT_OPTIONS.map((option) => (
              <CircleButton
                key={"international__" + option.value}
                value={option.value}
                text={option.label}
                selected={option.value === internationalFlightsYearly}
                onClick={handleInternationalFlightsSelection}
              />
            ))}
          </CircleButtonContainer>
        </CircleButtonGroupWithLabel>
      </UserInputSection>
    </>
  );
}

export default StepThree;
